import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import './modalHelper.css';

let resolver = null;

export function showModal(title, message, options = {}) {
  return new Promise((resolve) => {
    resolver = resolve;
    const event = new CustomEvent('show-modal', { detail: { title, message, options } });
    window.dispatchEvent(event);
  });
}

function ModalRoot() {
  const [open, setOpen] = useState(false);
  const [payload, setPayload] = useState({ title: '', message: '', options: {} });
  const modalCardRef = useRef(null);
  const previousActiveElementRef = useRef(null);

  const close = useCallback((result) => {
    setOpen(false);
    if (resolver) {
      resolver(result);
      resolver = null;
    }
  }, []);

  useEffect(() => {
    function handler(e) {
      setPayload(e.detail || { title: '', message: '', options: {} });
      setOpen(true);
    }
    window.addEventListener('show-modal', handler);
    return () => window.removeEventListener('show-modal', handler);
  }, []);

  const { title, message, options } = payload;
  const isDanger = options.isDanger || options.type === 'danger';
  const badgeType = isDanger ? 'danger' : (options.type === 'info' ? 'info' : 'primary');

  // Focus trap WAI-ARIA WCAG 2.1 e suporte a Escape
  useEffect(() => {
    if (!open) return;
    previousActiveElementRef.current = document.activeElement;

    // Focar o botão adequado
    const card = modalCardRef.current;
    if (card) {
      const cancelButton = card.querySelector('.saria-modal-btn-cancel');
      const confirmButton = card.querySelector('.saria-modal-btn-confirm-danger, .saria-modal-btn-confirm-primary');
      if (isDanger && cancelButton) {
        cancelButton.focus();
      } else if (confirmButton) {
        confirmButton.focus();
      }
    }

    function handleKeyDown(e) {
      if (e.key === 'Escape') {
        close(payload.options?.cancelText ? false : true);
        return;
      }

      if (e.key === 'Tab') {
        const cardNode = modalCardRef.current;
        if (!cardNode) return;
        const focusableElements = Array.from(
          cardNode.querySelectorAll('button:not([disabled]), [tabindex]:not([tabindex="-1"])')
        );
        if (focusableElements.length === 0) return;

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      if (previousActiveElementRef.current && previousActiveElementRef.current.focus) {
        try { previousActiveElementRef.current.focus(); } catch (_) {}
      }
    };
  }, [open, close, isDanger, payload.options]);

  if (!open) return null;

  return ReactDOM.createPortal(
    <div 
      className="saria-modal-backdrop" 
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          close(options.cancelText ? false : true);
        }
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="saria-modal-title"
      aria-describedby="saria-modal-desc"
    >
      <div className="saria-modal-card" ref={modalCardRef}>
        <div className="saria-modal-header">
          <div className={`saria-modal-icon-badge ${badgeType}`}>
            {isDanger ? (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            ) : (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                <path d="m9 12 2 2 4-4" />
              </svg>
            )}
          </div>
          <div className="saria-modal-header-content">
            <h3 id="saria-modal-title" className="saria-modal-title">{title}</h3>
          </div>
          <button 
            type="button" 
            className="saria-modal-close-btn" 
            onClick={() => close(options.cancelText ? false : true)}
            aria-label="Fechar"
          >
            &times;
          </button>
        </div>

        <div id="saria-modal-desc" className="saria-modal-body">
          {message}
        </div>

        <div className="saria-modal-footer">
          {options.cancelText && (
            <button 
              type="button" 
              onClick={() => close(false)} 
              className="saria-modal-btn saria-modal-btn-cancel"
              autoFocus={isDanger}
            >
              {options.cancelText}
            </button>
          )}
          <button 
            type="button" 
            onClick={() => close(true)} 
            className={`saria-modal-btn ${isDanger ? 'saria-modal-btn-confirm-danger' : 'saria-modal-btn-confirm-primary'}`}
            autoFocus={!isDanger || !options.cancelText}
          >
            {options.confirmText || 'OK'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default ModalRoot;
